'use client'

import { ComponentProps, ReactNode } from 'react'

import * as Styles from './PageHero.styles'

/** Container with slots for the content area of the page hero section. */
function PageHeroContent({
  desktopWidth = '100%',
  mobileWidth = '100%',
  title,
  subtitle,
  cta,
  children,
}: {
  /** The width that the content section should take up within the page hero section on desktop viewports. */
  desktopWidth?: '50%' | '100%'
  /** The width that the content section should take up within the page hero section on mobile viewports. */
  mobileWidth?: '50%' | '100%'
  /** The title text that should be displayed within the page hero section. See `PageHero.ContentTitle`. */
  title: ReactNode
  /** The subtitle text that should be displayed within the page hero section. See `PageHero.ContentSubtitle`. */
  subtitle?: ReactNode
  /** The CTA that should be rendered within the page hero section. See `PageHero.Cta`. */
  cta?: ReactNode
  /** Additional content that should be rendered within the page hero content section. */
  children?: ReactNode
}) {
  return (
    <Styles.Content $desktopWidth={desktopWidth} $mobileWidth={mobileWidth}>
      {title}
      {subtitle}
      {children}
      {cta}
    </Styles.Content>
  )
}

/** Container with slots for the media area of the page hero section. */
function PageHeroMedia({
  placement,
  image,
  video,
}: {
  /** The placement of the media section within the page hero. */
  placement: 'right' | 'left'
  /** The image to render within the media section of the page hero. */
  image?: ReactNode
  /** The video to render within the media section of the page hero. */
  video?: ReactNode
}) {
  return (
    <Styles.ImageWrapper $placement={placement}>
      {image}
      {video}
    </Styles.ImageWrapper>
  )
}

function PageHeroSubtitle({
  children,
  ...rest
}: ComponentProps<typeof Styles.ContentSubtitleWrapper>) {
  return (
    <Styles.ContentSubtitleWrapper {...rest}>
      <Styles.ContentSubtitle>{children}</Styles.ContentSubtitle>
    </Styles.ContentSubtitleWrapper>
  )
}

const PageHero = {
  Wrapper: Styles.Wrapper,
  Content: PageHeroContent,
  ContentTitle: Styles.ContentTitle,
  ContentSubtitle: PageHeroSubtitle,
  Cta: Styles.Cta,
  Media: PageHeroMedia,
  Skeleton: Styles.SkeletonLoader,
}

export default PageHero
